/**
 * @name AuthIntegrationService
 * @description Serviço para integração transparente com a API de autenticação
 * @author Gabriel M. Guimarães | gabrielmg7
 * @package src/services/auth/AuthIntegrationService.ts
 */

interface LoginIntegracaoParams {
     anoLetivo: string;
     matricula: string;
}

interface AuthIntegrationResponse {
     success: boolean;
     access_token?: string;
     refresh_token?: string;
     user?: {
          id: string;
          nome: string;
          email: string;
     };
     error?: string;
     responsavel?: {
          nome: string;
          cpf: string;
          codigo: string;
          aluno: {
               matricula: string;
               ano_letivo: string;
          };
     };
}

/**
 * Serviço para autenticação transparente via integração externa
 * Conecta com a API de autenticação (is-cantina-auth-api) 
 * para autenticar usuários vindos do sistema parceiro
 */
export class AuthIntegrationService {
     private readonly baseUrl = this.getApiBaseUrl(); // URL da API de autenticação
     
     /**
      * Determina a URL base da API de autenticação baseada no ambiente
      */
     private getApiBaseUrl(): string {
          if (import.meta.env.DEV) {
               return import.meta.env.VITE_AUTH_API_DEV_URL ?? '';
          }

          // Em produção, usar URL da API deployada
          return import.meta.env.VITE_AUTH_API_URL;
     }
     
     /**
      * Autentica usuário via integração com dados de matrícula
      * Endpoint: POST /integracaoEC/matricula/login
      */
     async authenticateViaIntegration(params: LoginIntegracaoParams): Promise<AuthIntegrationResponse> {
          try {
               const response = await fetch(`${this.baseUrl}/integracaoEC/matricula/login`, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                         anoLetivo: params.anoLetivo,
                         matricula: params.matricula,
                    }),
               });

               const data = await response.json();

               if (response.ok && data.access_token) {
                    return {
                         success: true,
                         access_token: data.access_token,
                         user: data.user,
                    };
               }

               // Caso específico: responsável sem email
               if (response.status === 422 && data.responsavel) {
                    return {
                         success: false,
                         error: data.error,
                         responsavel: data.responsavel,
                    };
               }

               // Outros erros
               return {
                    success: false,
                    error: data.error || 'Erro na autenticação via integração',
               };
          } catch (error) {
               console.error('Erro na integração de autenticação:', error);
               return {
                    success: false,
                    error: 'Erro de conectividade com o serviço de autenticação',
               };
          }
     }

     /**
      * Valida se um token de integração ainda é válido
      * Endpoint: GET /auth/validate
      */
     async validateIntegrationToken(token: string): Promise<{ valid: boolean; user?: any }> {
          try {
               const response = await fetch(`${this.baseUrl}/auth/validate`, {
                    method: 'GET',
                    headers: {
                         'Authorization': `Bearer ${token}`,
                    },
               });

               if (response.ok) {
                    const data = await response.json();
                    return {
                         valid: true,
                         user: data.data?.user,
                    };
               }

               return { valid: false };
          } catch (error) {
               console.error('Erro na validação do token:', error);
               return { valid: false };
          }
     }

     /**
      * Obtém dados completos do usuário autenticado
      * Endpoint: GET /auth/me
      */
     async getCurrentUser(token: string): Promise<{ success: boolean; user?: any; error?: string }> {
          try {
               const response = await fetch(`${this.baseUrl}/auth/me`, {
                    method: 'GET',
                    headers: {
                         'Authorization': `Bearer ${token}`,
                    },
               });

               if (response.ok) {
                    const data = await response.json();
                    return {
                         success: true,
                         user: data.data,
                    };
               }

               return {
                    success: false,
                    error: 'Erro ao obter dados do usuário',
               };
          } catch (error) {
               console.error('Erro ao obter dados do usuário:', error);
               return {
                    success: false,
                    error: 'Erro de conectividade',
               };
          }
     }

     /**
      * Faz logout do usuário na API de autenticação
      * Endpoint: POST /auth/logout
      */
     async logout(token: string): Promise<{ success: boolean }> {
          try {
               const response = await fetch(`${this.baseUrl}/auth/logout`, {
                    method: 'POST',
                    headers: {
                         'Authorization': `Bearer ${token}`,
                    },
               });

               return { success: response.ok };
          } catch (error) {
               console.error('Erro no logout:', error);
               return { success: false };
          }
     }
}

// Instância singleton do serviço
export const authIntegrationService = new AuthIntegrationService();
